/**
 * One session across every tab of this browser (round 9, B-18 and sign-out fix A).
 *
 * The automatic logoff (SEC-14) used to run per tab: a background tab nobody touched signed out a
 * person who was typing in another tab, audited it as idle and ended the session for both. Two
 * things are shared now:
 *
 * - **The last activity** (`localStorage`, `LAST_ACTIVITY_KEY`): a timestamp every tab writes on
 *   activity and reads before it warns or signs out, so the idle clock is the browser's, not the
 *   tab's. A timestamp only: never an address or a token.
 * - **Session events** (`BroadcastChannel`, `SESSION_CHANNEL`): "the person is still here" from
 *   an idle warning, and "the session ended" from any sign-out, so every other tab shows the same
 *   state at once instead of when its own clock or token runs out.
 *
 * Every access is guarded: blocked storage or a browser without `BroadcastChannel` falls back to
 * the per-tab behaviour, which is safe (it can only end a session early, never keep one open).
 */

export const LAST_ACTIVITY_KEY = "skydata-last-activity";
export const SESSION_CHANNEL = "skydata-session";
export const SESSION_ENDED_KEY = "skydata-session-ended";

/** Why a session ended, as `AuthContext.signOut` names it. */
export type SessionEndReason = "user" | "idle";

export type SessionEvent =
  /** An idle warning was answered "I'm still here" in another tab. */
  | { type: "continue"; at: number }
  /** Another tab ended the session; this one ends locally, without a second audit row. */
  | { type: "signed-out"; reason: SessionEndReason };

/** How far ahead of this tab's clock a shared timestamp may be before it counts as corrupt. */
const FUTURE_SKEW_MS = 5_000;

/**
 * The last activity any tab recorded, or null when none is readable. A value that is not a
 * positive number, or lies in the future (a skewed or forged write), is null too: fail closed,
 * a null clock is an expired one wherever the session is held to it (`idleLimitPassed`).
 */
export function readLastActivity(now: number = Date.now()): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw === null) return null;
    const at = Number(raw);
    if (!Number.isFinite(at) || at <= 0 || at > now + FUTURE_SKEW_MS) return null;
    return at;
  } catch {
    return null;
  }
}

/** Records activity for every tab, clamped to now. Never moves the clock backwards. */
export function writeLastActivity(at: number = Date.now()): void {
  const clamped = Math.min(at, Date.now());
  try {
    const current = readLastActivity();
    if (current !== null && current >= clamped) return;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(clamped));
  } catch {
    /* site data blocked: this tab keeps its own clock */
  }
}

/**
 * Forgets the shared clock: a session that ended has no idle time left to count, and the next
 * sign-in starts its own (review r9 #1).
 */
export function clearLastActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* site data blocked: nothing was shared */
  }
}

/**
 * Whether the browser's idle limit passed while no tab was open (review r9 #1): MSAL keeps its
 * tokens in localStorage, so closing every tab no longer ends the session, and a tab opened
 * hours later would otherwise count its own opening as activity and carry on.
 */
export function idleLimitPassed(idleMs: number, now: number = Date.now()): boolean {
  const last = readLastActivity(now);
  // Fail closed (round 9 review): a missing, corrupt or future clock is an expired one where the
  // session is held to it strictly (an MSAL session); the dev provider starts a missing clock.
  if (last === null) return policy.strict;
  return now - last >= idleMs;
}

/**
 * The session policy the API layer and the idle hook enforce, set by `AuthContext`: the idle
 * limit served by `/api/auth/config`, and whether a missing clock counts as expired (`strict`, an
 * MSAL session; the local dev provider is not strict, as its tests sign in without a clock).
 */
const policy = { idleMs: 30 * 60_000, strict: false, enforced: false };

export function setSessionPolicy(next: {
  idleMs: number;
  strict: boolean;
  enforced: boolean;
}): void {
  Object.assign(policy, next);
}

/** Key of the idle lock: set by an idle logoff, cleared only by an interactive sign-in. */
export const IDLE_LOCK_KEY = "skydata-idle-lock";

/**
 * Round 9 review ("one click, not zero"): after an idle logoff no tab may resume silently, not a
 * reload, a new or restored tab, nor a page from the back-forward cache. The lock is in
 * localStorage, so every tab and every later document sees it; while it is set no token is
 * acquired silently and the sign-in page asks for one click.
 */
export function setIdleLock(at: number = Date.now()): void {
  try {
    localStorage.setItem(IDLE_LOCK_KEY, String(at));
  } catch {
    /* storage blocked: MSAL cannot keep a session in it either */
  }
}

export function clearIdleLock(): void {
  try {
    localStorage.removeItem(IDLE_LOCK_KEY);
  } catch {
    /* nothing was stored */
  }
}

export function idleLocked(): boolean {
  try {
    return localStorage.getItem(IDLE_LOCK_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Whether the session is over by the idle rule right now: locked, or the browser's idle limit
 * passed by the wall clock. The API layer asks before every authenticated request, and the idle
 * hook on every wake (visibility, focus, pageshow), so a tab that slept past the deadline sends
 * nothing (round 9 review).
 */
export function sessionDeadlinePassed(now: number = Date.now()): boolean {
  if (!policy.enforced) return false;
  return idleLocked() || idleLimitPassed(policy.idleMs, now);
}

/**
 * Records that a tab ended the session now. Another tab's idle clock reads it before it signs
 * out on its own, so one idle logoff writes one `auth.sign_out` row, not one per tab.
 */
export function markSessionEnded(at: number = Date.now()): void {
  try {
    localStorage.setItem(SESSION_ENDED_KEY, String(at));
  } catch {
    /* site data blocked: each tab ends its own session */
  }
}

/** When a tab last ended the session, or null. */
export function readSessionEnded(): number | null {
  try {
    const raw = localStorage.getItem(SESSION_ENDED_KEY);
    if (raw === null) return null;
    const at = Number(raw);
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}

/**
 * Runs `task` in at most one tab at a time (Web Locks, `ifAvailable`); a tab that finds the lock
 * held runs `busy` instead. Without Web Locks the task runs, which is the per-tab behaviour. The
 * idle logoff uses it so tabs whose timers fire together write one audit row (review r9 #7).
 */
export async function inOneTab(
  name: string,
  task: () => Promise<void> | void,
  busy: () => Promise<void> | void,
): Promise<void> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks) {
    await task();
    return;
  }
  try {
    await locks.request(name, { ifAvailable: true }, async (lock) => {
      if (lock) await task();
      else await busy();
    });
  } catch {
    await task();
  }
}

function isSessionEvent(value: unknown): value is SessionEvent {
  if (typeof value !== "object" || value === null) return false;
  const event = value as { type?: unknown; at?: unknown; reason?: unknown };
  if (event.type === "continue") return typeof event.at === "number";
  if (event.type === "signed-out") return event.reason === "user" || event.reason === "idle";
  return false;
}

function newChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(SESSION_CHANNEL);
  } catch {
    return null;
  }
}

/** One listener's end of the channel: what it posts reaches every OTHER end, never itself. */
export interface SessionChannel {
  post: (event: SessionEvent) => void;
  close: () => void;
}

/**
 * Opens this listener's end of the session channel. Posting goes through the same end that
 * listens, because a `BroadcastChannel` delivers to every other object of its name, including
 * another one in this tab: an end that posted through a second object would hear itself.
 */
export function openSessionChannel(handler: (event: SessionEvent) => void): SessionChannel {
  const open = newChannel();
  if (!open) return { post: () => {}, close: () => {} };
  open.onmessage = (message: MessageEvent<unknown>) => {
    if (isSessionEvent(message.data)) handler(message.data);
  };
  return {
    post: (event) => {
      try {
        open.postMessage(event);
      } catch {
        /* the other tabs fall back to their own clocks */
      }
    },
    close: () => open.close(),
  };
}
